<script lang="ts">
  interface Props {
    value: number;
    onInput: (value: number) => void;
    onClear?: () => void;
    onBackspace?: () => void;
  }

  let { value, onInput, onClear, onBackspace }: Props = $props();
  
  // Interner String-State für die Eingabe
  // Initialisiere leer - wird durch $effect gesetzt
  let inputString = $state('');
  let lastValue = $state(value);
  let isInitialized = $state(false);
  let initialValue = $state(value); // Speichere initialen Wert
  
  // Synchronisiere mit value prop - nur wenn value von außen geändert wurde
  $effect(() => {
    if (!isInitialized || value !== lastValue) {
      // Initialisierung oder Value wurde von außen geändert
      if (value === 0) {
        inputString = '0';
      } else {
        // Konvertiere zu String mit Komma
        const str = value.toString().replace('.', ',');
        // Wenn es "12,00" ist, zeige nur "12" für bessere UX beim Start
        if (str.endsWith(',00') && !isInitialized) {
          inputString = str.replace(',00', '');
        } else {
          inputString = str;
        }
      }
      lastValue = value;
      if (!isInitialized) {
        initialValue = value; // Speichere initialen Wert beim ersten Mal
      }
      isInitialized = true;
    }
  });

  function updateValue() {
    // Konvertiere String zu Number
    const numValue = parseFloat(inputString.replace(',', '.')) || 0;
    lastValue = numValue;
    onInput(numValue);
  }

  function handleNumber(num: number) {
    // Wenn leer, "0" oder Wert gleich dem initialen Wert, ersetze komplett
    // Das bedeutet: erste Eingabe nach Öffnen ersetzt den Preis
    const initialStr = initialValue.toString().replace('.', ',');
    const currentStr = inputString;
    
    // Prüfe ob wir noch beim initialen Wert sind (erste Eingabe)
    const isInitialValue = currentStr === initialStr || 
                          currentStr === initialStr.replace(',00', '') ||
                          currentStr === '';
    
    if (currentStr === '' || currentStr === '0' || isInitialValue) {
      // Erste Eingabe: Ersetze komplett
      inputString = num.toString();
    } else {
      // Weitere Eingaben: Füge Zahl hinzu
      inputString = inputString + num.toString();
    }
    
    // Maximal 2 Nachkommastellen nach dem Komma
    const parts = inputString.split(',');
    if (parts.length > 1 && parts[1].length > 2) {
      parts[1] = parts[1].substring(0, 2);
      inputString = parts.join(',');
    }
    
    updateValue();
  }

  function handleComma() {
    // Nur ein Komma erlauben
    if (!inputString.includes(',')) {
      if (inputString === '' || inputString === '0') {
        inputString = '0,';
      } else {
        inputString = inputString + ',';
      }
      // Update auch bei Komma, damit der Wert korrekt ist
      updateValue();
    }
  }

  function handleBackspace() {
    if (onBackspace) {
      // Verwende custom Backspace Handler - aber arbeite trotzdem mit inputString
      // Entferne letzte Ziffer vom inputString
      if (inputString.length > 1 && inputString !== '0') {
        inputString = inputString.slice(0, -1);
        // Wenn nach Backspace nur Komma übrig bleibt, entferne es auch
        if (inputString.endsWith(',')) {
          inputString = inputString.slice(0, -1);
        }
        // Wenn leer, setze auf 0
        if (inputString === '') {
          inputString = '0';
        }
        updateValue();
      } else {
        // Nur noch eine Ziffer oder "0" - setze auf 0
        inputString = '0';
        updateValue();
      }
      // Rufe auch den custom Handler auf
      onBackspace();
    } else {
      // Standard: Letzte Ziffer entfernen
      if (inputString.length > 1 && inputString !== '0') {
        inputString = inputString.slice(0, -1);
        // Wenn nach Backspace nur Komma übrig bleibt, entferne es auch
        if (inputString.endsWith(',')) {
          inputString = inputString.slice(0, -1);
        }
        // Wenn leer, setze auf 0
        if (inputString === '') {
          inputString = '0';
        }
      } else {
        inputString = '0';
      }
      updateValue();
    }
  }

  function handleClear() {
    if (onClear) {
      onClear();
      inputString = '';
    } else {
      inputString = '0';
      onInput(0);
    }
  }
</script>

<div class="grid grid-cols-3 gap-1 md:gap-1.5">
  <!-- Row 1: 1, 2, 3 -->
  <button
    type="button"
    onclick={() => handleNumber(1)}
    class="h-10 md:h-12 bg-white border-2 border-gray-300 rounded-lg text-base md:text-lg font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
  >
    1
  </button>
  <button
    type="button"
    onclick={() => handleNumber(2)}
    class="h-10 md:h-12 bg-white border-2 border-gray-300 rounded-lg text-base md:text-lg font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
  >
    2
  </button>
  <button
    type="button"
    onclick={() => handleNumber(3)}
    class="h-10 md:h-12 bg-white border-2 border-gray-300 rounded-lg text-base md:text-lg font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
  >
    3
  </button>

  <!-- Row 2: 4, 5, 6 -->
  <button
    type="button"
    onclick={() => handleNumber(4)}
    class="h-10 md:h-12 bg-white border-2 border-gray-300 rounded-lg text-base md:text-lg font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
  >
    4
  </button>
  <button
    type="button"
    onclick={() => handleNumber(5)}
    class="h-10 md:h-12 bg-white border-2 border-gray-300 rounded-lg text-base md:text-lg font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
  >
    5
  </button>
  <button
    type="button"
    onclick={() => handleNumber(6)}
    class="h-10 md:h-12 bg-white border-2 border-gray-300 rounded-lg text-base md:text-lg font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
  >
    6
  </button>

  <!-- Row 3: 7, 8, 9 -->
  <button
    type="button"
    onclick={() => handleNumber(7)}
    class="h-10 md:h-12 bg-white border-2 border-gray-300 rounded-lg text-base md:text-lg font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
  >
    7
  </button>
  <button
    type="button"
    onclick={() => handleNumber(8)}
    class="h-10 md:h-12 bg-white border-2 border-gray-300 rounded-lg text-base md:text-lg font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
  >
    8
  </button>
  <button
    type="button"
    onclick={() => handleNumber(9)}
    class="h-10 md:h-12 bg-white border-2 border-gray-300 rounded-lg text-base md:text-lg font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
  >
    9
  </button>

  <!-- Row 4: Komma, 0, Rücktaste -->
  <button
    type="button"
    onclick={handleComma}
    class="h-10 md:h-12 bg-white border-2 border-gray-300 rounded-lg text-base md:text-lg font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
  >
    ,
  </button>
  <button
    type="button"
    onclick={() => handleNumber(0)}
    class="h-10 md:h-12 bg-white border-2 border-gray-300 rounded-lg text-base md:text-lg font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
  >
    0
  </button>
  <button
    type="button"
    onclick={handleBackspace}
    class="h-10 md:h-12 bg-red-50 border-2 border-red-300 rounded-lg text-base md:text-lg font-semibold text-red-700 hover:bg-red-100 active:bg-red-200 touch-manipulation flex items-center justify-center"
    aria-label="Löschen"
  >
    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"
      />
    </svg>
  </button>
</div>

